/**
 * Parses a TCX XML string into a structured JavaScript object.
 * Relies on the browser's DOMParser.
 *
 * @param {string} xmlString The raw TCX XML content as a string.
 * @returns {{metadata: object, waypoints: Array, bounds: object|null, tracks: Array}} The parsed data object, or an empty structure on error.
 */

// --- Safe Helper Functions ---
const safeGetTextContent = (node, tagName) => {
  if (!node) return null;
  const element = node.getElementsByTagName(tagName)[0];
  return element ? element.textContent?.trim() || null : null;
};

const safeGetValueContent = (node, tagName) => {
  // Helper for tags like <HeartRateBpm><Value>140</Value></HeartRateBpm>
  if (!node) return null;
  const parent = node.getElementsByTagName(tagName)[0];
  if (!parent) return null;
  return safeGetTextContent(parent, "Value");
};

const safeParseFloat = (text) => {
  if (text === null || text === undefined || text === "") return null;
  const number = parseFloat(text);
  return isNaN(number) ? null : number;
};

const safeParseInt = (text) => {
  if (text === null || text === undefined || text === "") return null;
  const number = parseInt(text, 10);
  return isNaN(number) ? null : number;
};

// --- Main Parsing Function ---
export const parseTCX = (xmlString) => {
  if (typeof xmlString !== "string" || !xmlString.trim().startsWith("<")) {
    console.error("Input to parseTCX is not a valid XML string!");
    return { metadata: {}, waypoints: [], bounds: null, tracks: [] };
  }

  const parser = new DOMParser();
  let xmlDoc;
  try {
    xmlDoc = parser.parseFromString(xmlString, "application/xml");
  } catch (parseError) {
    console.error("DOMParser failed:", parseError);
    return { metadata: {}, waypoints: [], bounds: null, tracks: [] };
  }

  const parserErrorNode = xmlDoc.querySelector("parsererror");
  if (parserErrorNode) {
    console.warn("TCX XML Parsing Warning:", parserErrorNode.textContent);
    // Allow proceeding despite potential minor errors
  }

  try {
    // TCX doesn't have a direct "metadata" block like GPX, but we can extract Author/Creator from Activity
    const metadata = getMetadata(xmlDoc);
    
    // TCX usually contains Activities -> Activity -> Lap -> Track -> Trackpoint
    // We will map each Activity to a "Track" in our internal model
    const tracks = getActivities(xmlDoc);
    
    // Calculate bounds from all tracks since TCX doesn't usually have a header bounds
    const bounds = calculateBounds(tracks);

    // TCX doesn't standardly support standalone waypoints (CoursePoints exist but are different)
    // We can parse CoursePoints as waypoints if needed, but for now we'll return empty or implement if requested.
    const waypoints = getCoursePoints(xmlDoc);

    return {
      metadata,
      waypoints,
      bounds,
      tracks,
    };
  } catch (extractionError) {
    console.error("Error during TCX data extraction:", extractionError);
    return { metadata: {}, waypoints: [], bounds: null, tracks: [] };
  }
};

// --- Extraction Helpers ---

const getMetadata = (xmlDoc) => {
  const metadata = {};
  // Try to find Author in the first Activity
  const activity = xmlDoc.getElementsByTagName("Activity")[0];
  if (activity) {
    // Creator is often available
    const creator = activity.getElementsByTagName("Creator")[0];
    if (creator) {
      metadata.author = {
        name: safeGetTextContent(creator, "Name"),
        email: null,
        link: null,
      };
    }
  }
  return metadata;
};

const calculateBounds = (tracks) => {
  let minLat = Infinity;
  let minLon = Infinity;
  let maxLat = -Infinity;
  let maxLon = -Infinity;
  let hasPoints = false;

  tracks.forEach(track => {
    track.trackData.forEach(p => {
      if (p.lat !== null && p.lng !== null) {
        hasPoints = true;
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLon) minLon = p.lng;
        if (p.lng > maxLon) maxLon = p.lng;
      }
    });
  });

  if (!hasPoints) return null;

  return { minLat, minLon, maxLat, maxLon };
};

const getActivities = (xmlDoc) => {
  const tracks = [];
  const activities = xmlDoc.getElementsByTagName("Activity");

  for (let i = 0; i < activities.length; i++) {
    const activity = activities[i];
    const sport = activity.getAttribute("Sport");
    const id = safeGetTextContent(activity, "Id");
    
    const trackInfo = {
      name: id || `Activity ${i + 1}`,
      type: sport,
      desc: `TCX Activity - ${sport}`,
    };

    // TCX Activities have Laps. We usually want to flatten these into one continuous track
    // or keep them as segments. For compatibility with the GPX parser structure, 
    // we will flatten all points from all laps in this activity into one trackData array.
    // If we wanted to support segments, we would use trkseg logic, but here we'll just concat.
    const trackData = getTrackPointsFromActivity(activity);

    if (trackData.length > 0) {
      tracks.push({ trackInfo, trackData });
    }
  }
  return tracks;
};

const getTrackPointsFromActivity = (activityNode) => {
  const allPoints = [];
  const laps = activityNode.getElementsByTagName("Lap");

  for (let i = 0; i < laps.length; i++) {
    const lap = laps[i];
    const track = lap.getElementsByTagName("Track")[0]; // Each Lap has one Track
    if (track) {
      const points = track.getElementsByTagName("Trackpoint");
      for (let j = 0; j < points.length; j++) {
        const pointData = parsePoint(points[j]);
        if (pointData) {
          allPoints.push(pointData);
        }
      }
    }
  }
  return allPoints;
};

const parsePoint = (pointNode) => {
  // TCX Position
  const position = pointNode.getElementsByTagName("Position")[0];
  let lat = null;
  let lng = null;
  
  if (position) {
    lat = safeParseFloat(safeGetTextContent(position, "LatitudeDegrees"));
    lng = safeParseFloat(safeGetTextContent(position, "LongitudeDegrees"));
  }

  // If no position, it might be a sensor-only point (e.g. indoor cycling). 
  // The GPX parser skips points without lat/lon. We will do the same for now to maintain map compatibility.
  if (lat === null || lng === null) {
    return null;
  }

  const time = safeGetTextContent(pointNode, "Time");
  const ele = safeParseFloat(safeGetTextContent(pointNode, "AltitudeMeters"));
  const distance = safeParseFloat(safeGetTextContent(pointNode, "DistanceMeters"));

  // Heart Rate
  // <HeartRateBpm><Value>140</Value></HeartRateBpm>
  let heartRate = safeParseInt(safeGetValueContent(pointNode, "HeartRateBpm"));

  // Cadence
  // Standard TCX: <Cadence>90</Cadence>
  let cadence = safeParseInt(safeGetTextContent(pointNode, "Cadence"));

  // Extensions (Watts, Speed, RunCadence)
  // Often found in <Extensions><TPX xmlns="..."><Speed>...</Speed><Watts>...</Watts></TPX></Extensions>
  let power = null;
  let speed = null;

  const extensions = pointNode.getElementsByTagName("Extensions")[0];
  if (extensions) {
    const tpx = extensions.getElementsByTagName("TPX")[0] || extensions.getElementsByTagName("ns3:TPX")[0]; // Handle namespaces loosely
    if (tpx) {
        const speedVal = safeGetTextContent(tpx, "Speed");
        if (speedVal) speed = safeParseFloat(speedVal);

        const wattsVal = safeGetTextContent(tpx, "Watts");
        if (wattsVal) power = safeParseInt(wattsVal);
        
        // Sometimes Cadence is in RunCadence
        if (cadence === null) {
             const runCadence = safeGetTextContent(tpx, "RunCadence");
             if (runCadence) cadence = safeParseInt(runCadence);
        }
    }
  }

  return {
    lat,
    lng,
    ele,
    time,
    heartrate: heartRate,
    cadence,
    power, // TCX often has power
    distance, // TCX often has cumulative distance
    speed
  };
};

const getCoursePoints = (xmlDoc) => {
  // Map TCX CoursePoints to Waypoints
  const waypoints = [];
  const coursePoints = xmlDoc.getElementsByTagName("CoursePoint");

  for (let i = 0; i < coursePoints.length; i++) {
    const cp = coursePoints[i];
    const position = cp.getElementsByTagName("Position")[0];
    let lat = null;
    let lng = null;

    if (position) {
      lat = safeParseFloat(safeGetTextContent(position, "LatitudeDegrees"));
      lng = safeParseFloat(safeGetTextContent(position, "LongitudeDegrees"));
    }

    if (lat !== null && lng !== null) {
      waypoints.push({
        lat,
        lng,
        name: safeGetTextContent(cp, "Name"),
        time: safeGetTextContent(cp, "Time"),
        type: safeGetTextContent(cp, "PointType"),
        desc: safeGetTextContent(cp, "Notes"),
      });
    }
  }
  return waypoints;
};
